import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import {
  Heading,
  Input,
  Button,
  ButtonCircle,
  Message,
  Text,
  Select as NormalSelect,
  Divider,
} from 'rebass';
import { Flex, Box } from 'reflexbox';
import { connect } from 'react-redux';
import axios from 'axios';
import { createFilter } from 'react-select';

import SharedConfig from 'shared/config';
import {
  isRingOrTrinket,
  slotMap,
  invertedSlotMap,
  isEnchantable,
  getEnhancementsForSlotNameAndSpec,
  canRollSecondary,
  secondaryBonuses,
  possibleSockets,
  hasCraftedStats,
  CRAFTED_STAT_COMBOS,
  getPossibleEmbellishments,
} from 'shared/items';
import {
  getGemInfo,
} from 'shared/gems';
import {
  activeLoadout,
  getSpecInfo,
  getSpecId,
} from 'shared/character';
import GameData, { load as loadGameData } from 'shared/gameData';
import { EXPANSIONS } from 'shared/constants';

import config from 'frontend/config';
import SimcVersionSingleton from 'frontend/simcVersion';

import actions from 'frontend/state/actions';
import { selectors as gearsetsSelectors } from 'frontend/state/reducers/simbot/gearsets';
import { selectors as savedGearsetsSelectors } from 'frontend/state/reducers/simbot/savedGearsets';
import { selectors as selectedSavedGearsetSelectors } from 'frontend/state/reducers/simbot/selectedSavedGearset';
import { selectors as formSelectors } from 'frontend/state/reducers/simbot/form';
import { selectors as simbotSelectors } from 'frontend/state/reducers/simbot';
import { selectors as localeSelectors } from 'frontend/state/reducers/locale';

import GearStrip from 'frontend/components/Simbot/GearStrip';

import {
  Icon,
  If,
  WowIcon,
  BlurUpdateInput,
  Loader,
  ReactSelect,
} from 'frontend/components';

const ITEM_OVERRIDES = [{
  id: 167555,
  hideItemLevel: true,
  deriveItemLevelFromPunchcards: true,
  note: 'Pocket-Sized Computation Device item level will be derived from punchcards',
}];

const fetchOptions = async (input, classId, specId, plvl, locale, callback) => {
  if (!input) {
    callback(null);
  }

  try {
    const result = await axios.get(
      `/api/item/${encodeURIComponent(input)}`,
      {
        params: { classId, specId, plvl, locale },
        headers: { 'x-simc-version': SimcVersionSingleton.get() },
      },
    );
    const options = result.data.map(match => ({
      label: match.name,
      value: match.id,
      ...match,
    }));
    callback(options);
  } catch (err) {
    callback(null);
  }
};

const debouncedFetchOptions = _.debounce(fetchOptions, 300);

const ItemSelectRenderer = option => {
  return (
    <Flex align="center" style={{ width: '190px' }}>
      <Box pr={1} style={{ height: '24px' }}>
        <WowIcon
          type="item"
          id={option.id}
          icon={option.icon}
          size={24}
          quality={option.quality}
          noLink
        />
      </Box>
      <Box>
        <Text
          color={config.colors.items[option.quality]}
          bold
          style={{
            width: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whitespace: 'nowrap',
            lineHeight: 1,
          }}
        >
          {option.label}
        </Text>
        <If
          condition={option.expansion < SharedConfig.currentExpansion}
          then={() => (
            <Text small color={config.colors.midgray}>Previous Expansion ({EXPANSIONS[option.expansion]?.name})</Text>
          )}
        />
      </Box>
    </Flex>
  );
};

const SavedGearsetRenderer = option => (
  <Box>
    <Box>
      {option.label}
    </Box>
    <Box>
      <Flex>
        {option.data.items && option.data.items.map(item => {
          if (item.item) {
            return (
              <Box pr={1} key={item.id}>
                <WowIcon
                  icon={item.item.icon}
                  size="small"
                  id={item.item.id}
                  type="item"
                  quality={item.item.quality}
                />
              </Box>
            );
          }
          return null;
        })}
      </Flex>
    </Box>
  </Box>
);

class SaveGearsetButton extends React.Component {
  static propTypes = {
    onClick: PropTypes.func.isRequired,
  };

  state = {
    clicked: false,
  };

  onClick = () => {
    this.props.onClick();
    this.setState({ clicked: true });
    _.delay(() => this.setState({ clicked: false }), 1000);
  };

  render() {
    const { clicked } = this.state;
    return (
      <Button
        mr={1}
        p={1}
        theme="secondary"
        onClick={clicked ? null : this.onClick}
        style={{ width: '140px' }}
      >
        {clicked ? 'Saved ✓' : 'Save Gearset'}
      </Button>
    );
  }
}

class ItemChooser extends React.Component { // eslint-disable-line react/no-multi-comp
  static propTypes = {
    gearsets: PropTypes.arrayOf(PropTypes.object).isRequired,
    savedGearsets: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedSavedGearset: PropTypes.string.isRequired,
    character: PropTypes.shape({
      class: PropTypes.number,
    }),
    equippedGear: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    baseActorName: PropTypes.string.isRequired,
    defaultBaseActorName: PropTypes.string.isRequired,
    locale: PropTypes.shape({ locale: PropTypes.string }).isRequired,

    addGearset: PropTypes.func.isRequired,
    removeGearset: PropTypes.func.isRequired,
    duplicateGearset: PropTypes.func.isRequired,
    resetGearsets: PropTypes.func.isRequired,
    saveGearset: PropTypes.func.isRequired,
    deleteSavedGearset: PropTypes.func.isRequired,
    chooseSavedGearset: PropTypes.func.isRequired,
    addItem: PropTypes.func.isRequired,
    removeItem: PropTypes.func.isRequired,
    onChangeGearsetName: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onChangeSlot: PropTypes.func.isRequired,
    onChangeItemLevel: PropTypes.func.isRequired,
    onChangeGem: PropTypes.func.isRequired,
    onChangeEnchant: PropTypes.func.isRequired,
    onChangeSecondary: PropTypes.func.isRequired,
    onChangeAffix: PropTypes.func.isRequired,
    onChangeHand: PropTypes.func.isRequired,
    onChangeCraftedStats: PropTypes.func.isRequired,
    onChangeEmbellishment: PropTypes.func.isRequired,
    inputForm: PropTypes.func.isRequired,
  };

  static defaultProps = {
    character: null,
  };

  state = {
    loading: false,
  };

  componentDidMount() {
    // make sure we set up an initial item on mount
    if (this.props.gearsets.length === 0) {
      this.props.addGearset();
    }
    this.fetchBonuses();
  }

  fetchBonuses = async () => {
    this.setState({ loading: true });
    await loadGameData('Bonuses');
    this.setState({ loading: false });
  };


  render() {
    const {
      gearsets,
      savedGearsets,
      selectedSavedGearset,
      character,
      equippedGear,
      baseActorName,
      defaultBaseActorName,
      locale,

      addGearset,
      removeGearset,
      duplicateGearset,
      resetGearsets,
      saveGearset,
      chooseSavedGearset,
      deleteSavedGearset,
      addItem,
      removeItem,
      onChangeGearsetName,
      onChange,
      onChangeSlot,
      onChangeItemLevel,
      onChangeGem,
      onChangeEnchant,
      onChangeSecondary,
      onChangeAffix,
      onChangeHand,
      onChangeCraftedStats,
      onChangeEmbellishment,

      inputForm,
    } = this.props;

    let specDetails = {};
    if (!character) {
      return (
        <div style={{ minHeight: 400 }}>
          <Message p={1} theme="warning">
            Load your character from the Armory or SimC addon above
          </Message>
        </div>
      );
    }

    const loadout = activeLoadout(character);
    const charClass = character?.class;
    const specName = _.snakeCase(_.get(loadout, 'talents.specName'));
    specDetails = getSpecInfo(charClass, specName);

    if (this.state.loading) {
      return <Loader />;
    }

    return (
      <Box mb={2}>
        <Heading level={4} mb={1}>
          Compare Sets of Gear
        </Heading>
        <Box>
          <Heading level={5}>Currently Equipped</Heading>
          <Box sm={12} md={3}>
            <Input
              label="Currently Equipped Gearset Name"
              name="baseActorName"
              value={baseActorName}
              maxLength={500}
              placeholder={defaultBaseActorName}
              onChange={e => inputForm('baseActorName', e.target.value)}
              autoComplete="off"
              color={config.color}
              backgroundColor={config.colors.primary}
            />
          </Box>
          <If condition={!_.isEmpty(equippedGear)}>
            <GearStrip
              equippedGear={equippedGear}
              classId={charClass}
              specName={specName}
              showEmptySlots
            />
          </If>
        </Box>
        <Divider />
        <Text my={2}>
          Each gearset starts as a copy of your currently equipped gear. Enter items you wish to swap out
        </Text>
        <Button
          theme="warning"
          onClick={() => {
            resetGearsets();
            // need to add an empty placeholder
            addGearset();
          }}
          mb={2}
        >
          Clear All Gearsets
        </Button>
        {gearsets.map(gearset => {
          const dupeSlots = _.map(
            _.pickBy(
              _.countBy(
                _.map(
                  _.filter(gearset.items, it => it.item),
                  it => {
                    if (it.itemHand === 'off_hand') {
                      return invertedSlotMap.off_hand;
                    }
                    return parseInt(invertedSlotMap[slotMap[it.item.inventoryType]], 10);
                  },
                ),
              ),
              (slotCount, slot) => {
                const slotName = slotMap[slot];
                let threshold = 1;
                if (_.includes(['finger', 'trinket'], slotName)) {
                  threshold = 2;
                }
                return slotCount > threshold;
              },
            ),
            (count, slotId) => slotMap[slotId],
          );

          return (
            <Box key={gearset.id}>
              <Box sm={12} md={6}>
                <Input
                  label="Gearset Name"
                  name="gearsetName"
                  maxLength={512}
                  placeholder={gearset.defaultName}
                  value={gearset.name}
                  onChange={e => onChangeGearsetName(gearset.id, e.target.value)}
                  autoComplete="off"
                  color={config.color}
                  backgroundColor={config.colors.primary}
                />
              </Box>
              <Box
                ml={0}
                pl={3}
                pb={0}
                mb={0}
              >
                <If condition={!_.isEmpty(equippedGear)}>
                  <Box mb={1} mt={1} pb={0} pt={0}>
                    <GearStrip
                      equippedGear={equippedGear}
                      overrideItems={gearset.items}
                      classId={_.get(character, 'class')}
                      specName={specName}
                      showEmptySlots
                    />
                  </Box>
                </If>
                <If condition={dupeSlots.length > 0}>
                  <Flex align="center" mt={1}>
                    <Box>
                      <ButtonCircle
                        size={24}
                        color={config.colors.black}
                        backgroundColor={config.colors.warning}
                        style={{
                          minHeight: '24px',
                          lineHeight: '24px',
                        }}
                        mr={1}
                      >
                        <Icon name="warning" size={16} />
                      </ButtonCircle>
                    </Box>
                    <Box>
                      Your gearset contains multiple items for these slots: {dupeSlots.join(', ')}
                    </Box>
                  </Flex>
                </If>
                {gearset.items.map(item => {
                  let bonuses;
                  let affixOptions;
                  let itemDefaultOption;
                  if (item.item) {
                    const chanceBonusList = item.item.chanceBonusList;
                    bonuses = _.filter(
                      _.map(chanceBonusList, bonusId => GameData.Bonuses[bonusId]),
                      b => b && b.name,
                    );
                    affixOptions = _.map(bonuses, (bonus => ({
                      value: bonus.id,
                      label: (
                        <Box>
                          {bonus.name}
                          <Text small>({bonus.stats.replace(/\[[0-9.]+\]/g, '')})</Text>
                        </Box>
                      ),
                      bonus,
                    })));
                    itemDefaultOption = {
                      label: item.name,
                      value: item.id,
                      ...item.item,
                    };
                  }
                  const sockets = possibleSockets(item.item);
                  const itemOverrides = _.find(ITEM_OVERRIDES, { id: _.get(item, 'item.id') });

                  const embellishments = getPossibleEmbellishments(item.item);

                  return (
                    <Flex col={12} wrap key={item.id} align="center" mt={0}>

                      {/* Remove Item Button */}

                      <If
                        condition={gearset.items.length > 1}
                        then={() => (
                          <Box pr={1} pt={1}>
                            <ButtonCircle
                              onClick={() => removeItem(gearset.id, item.id)}
                              size={24}
                              color={config.colors.black}
                              backgroundColor={config.colors.warning}
                              style={{
                                minHeight: '24px',
                                lineHeight: '24px',
                              }}
                            >
                              <Icon name="close" size={16} />
                            </ButtonCircle>
                          </Box>
                        )}
                      />

                      {/* Item Search */}

                      <Box
                        pr={1}
                        lg={3}
                        md={3}
                        sm={12}
                        style={{
                          minWidth: '250px',
                          marginBottom: '20px',
                        }}
                      >
                        <ReactSelect
                          async
                          label="Item Name"
                          name="itemName"
                          noOptionsMessage={() => 'Start typing to search for an item'}
                          onChange={value => {
                            onChange(gearset.id, item.id, value);
                          }}
                          value={itemDefaultOption}
                          defaultOptions={item.item && []}
                          loadOptions={(inputValue, callback) => {
                            let input = inputValue;
                            if (_.size(inputValue) > 256) {
                              input = inputValue.substring(0, 256);
                            }
                            debouncedFetchOptions(
                              input,
                              _.get(character, 'class', null),
                              getSpecId(character),
                              _.get(character, 'level', null),
                              locale.locale,
                              callback,
                            );
                            /*
                            console.log('loadOptions');
                            if (val !== '') {
                              // input is coming from user typing so use debounce and the incoming val
                              console.log('user typing');
                            } else if (item.item && item.item.name) {
                              // input came from duplicated gearset, do NOT debounce, immediate fetch item
                              console.log('load gearset');
                              fetchOptions(
                                item.item.name,
                                _.get(character, 'class', null),
                                getSpecId(character),
                                _.get(character, 'level', null),
                                locale.locale,
                                cb,
                              );
                            } else {
                              // unclear if this happens but return empty options
                              console.log('????');
                              cb([]);
                            }
                            */
                          }}
                          isClearable
                          formatOptionLabel={ItemSelectRenderer}
                          blurInputOnSelect
                          backgroundColor="#222"
                          autoComplete="off"
                          menuWidth={400}
                        />
                      </Box>

                      {/* Affix */}

                      <If
                        condition={bonuses && bonuses.length > 0}
                        then={() => (
                          <Box
                            pr={1}
                            lg={3}
                            md={3}
                            sm={12}
                            style={{
                              minWidth: '200px',
                              marginBottom: '21px',
                            }}
                          >
                            <ReactSelect
                              label="Affix"
                              name="itemAffix"
                              value={_.find(affixOptions, { value: item.itemAffix })}
                              options={affixOptions}
                              filterOption={(option, filter) => {
                                if (filter) {
                                  const { bonus } = option.data;
                                  return (
                                    _.includes(bonus.name.toLowerCase(), filter.toLowerCase())
                                    || _.includes(bonus.stats.toLowerCase(), filter.toLowerCase())
                                  );
                                }
                                return true;
                              }}
                              onChange={bonus => onChangeAffix(gearset.id, item.id, bonus ? bonus.value : null)}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                              blurInputOnSelect
                            />
                          </Box>
                        )}
                      />

                      {/* Secondary Stat */}

                      <If
                        condition={canRollSecondary(item.item)}
                        then={() => (
                          <Box pr={1} lg={2} md={2} sm={12} style={{ minWidth: '150px' }}>
                            <NormalSelect
                              label="Secondary Stat"
                              name="itemSecondary"
                              value={item.itemSecondary}
                              options={_.map(secondaryBonuses, (bonus => ({
                                value: bonus.bonusId,
                                children: bonus.name,
                              })))}
                              onChange={e => onChangeSecondary(gearset.id, item.id, e.target.value)}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                            />
                          </Box>
                        )}
                      />

                      {/* Trinket/Ring slot */}

                      <If
                        condition={isRingOrTrinket(item.item)}
                        then={() => (
                          <Box pr={1} lg={2} md={2} sm={12} style={{ minWidth: '150px' }}>
                            <NormalSelect
                              label="Slot"
                              name="itemSlot"
                              value={item.itemSlot}
                              options={_.map(_.range(2), (i) => {
                                const slotNum = i + 1;
                                const slotName = slotMap[item.item.inventoryType];
                                return {
                                  value: `${slotName}${slotNum}`,
                                  children: `${_.capitalize(slotName)} ${slotNum}`,
                                };
                              })}
                              onChange={e => onChangeSlot(gearset.id, item.id, e.target.value)}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                            />
                          </Box>
                        )}
                      />

                      {/* Item Hand */}

                      <If
                        condition={
                          item.item &&
                          (
                            (item.item.inventoryType === 13 && specDetails && specDetails.dualWield)
                            ||
                            (item.item.inventoryType === 17 && specDetails && specDetails.dualWield2h)
                          )
                        }
                        then={() => (
                          <Box pr={1} lg={2} md={2} sm={12}>
                            <NormalSelect
                              label="Slot"
                              name="itemHandSlot"
                              value={item.itemHand}
                              options={[{
                                value: 'main_hand',
                                children: 'Main Hand',
                              }, {
                                value: 'off_hand',
                                children: 'Off Hand',
                              }]}
                              onChange={e => onChangeHand(gearset.id, item.id, e.target.value)}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                            />
                          </Box>
                        )}
                      />

                      {/* Item Level */}

                      <If
                        condition={item.item && !_.get(itemOverrides, 'hideItemLevel')}
                        then={() => (
                          <Box pr={1} lg={1} md={2} sm={12}>
                            <BlurUpdateInput
                              label="Item Level"
                              name="itemLevel"
                              type="number"
                              value={item.itemLevel}
                              onChange={e => onChangeItemLevel(gearset.id, item.id, e.target.value)}
                            />
                          </Box>
                        )}
                      />

                      <If
                        condition={item?.item && hasCraftedStats(item.item)}
                        then={() => (
                          <Box pr={1} lg={2} md={2} sm={12}>
                            <NormalSelect
                              label="Secondary Stats"
                              name="itemCraftedStats"
                              value={item.craftedStats}
                              onChange={e => onChangeCraftedStats(gearset.id, item.id, e.target.value)}
                              options={_.union(
                                [{
                                  value: '',
                                  children: 'None',
                                }],
                                _.map(CRAFTED_STAT_COMBOS, combo => ({
                                  value: combo.value,
                                  children: combo.name,
                                })),
                              )}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                            />
                          </Box>
                        )}
                      />

                      {/* Gems */}

                      <If
                        condition={_.size(sockets) > 0}
                        then={() => (
                          _.map(sockets, (socketType, slotIdx) => {
                            const slotField = `itemGem${slotIdx}`;
                            const value = item[slotField];
                            const validGems = _.orderBy(
                              _.map(
                                _.filter(getGemInfo(), g => {
                                  return g.type === socketType;
                                }),
                                g => ({
                                  ...g,
                                  craftingQuality: g.craftingQuality || 0,
                                  expansion: g.expansion || 0,
                                }),
                              ),
                              ['expansion', 'craftingQuality', 'quality'],
                              ['desc', 'desc', 'desc'],
                            );
                            const socketName = _.startCase(_.lowerCase(socketType));
                            if (_.size(validGems) > 0) {
                              const gemOptions = _.map(validGems, gem => ({
                                value: gem.itemId,
                                label: (
                                  <Flex align="center">
                                    <If
                                      condition={gem.itemId}
                                      then={() => (
                                        <Box mr={1}>
                                          <WowIcon
                                            type="item"
                                            id={gem.itemId}
                                            size={22}
                                            noLink
                                            craftingQuality={gem.craftingQuality}
                                            quality={gem.quality}
                                          />
                                        </Box>
                                      )}
                                    />
                                    <Text size={14} color={config.colors.white}>
                                      {gem.name || gem.value}
                                    </Text>
                                  </Flex>
                                ),
                                gem,
                              }));
                              return (
                                <Box
                                  pr={1}
                                  mb={2}
                                  lg={2}
                                  md={2}
                                  sm={12}
                                  key={`gem-${slotIdx}`}
                                  style={{
                                    marginBottom: '20px',
                                  }}
                                >
                                  <ReactSelect
                                    label={`Gem (${socketName})`}
                                    name="itemGem"
                                    value={_.find(gemOptions, { value })}
                                    options={_.union(
                                      [{ value: '', label: 'No Socket' }],
                                      gemOptions,
                                    )}
                                    onChange={opt => {
                                      onChangeGem(gearset.id, item.id, slotIdx, opt.value);
                                    }}
                                    autoComplete="off"
                                    color={config.color}
                                    backgroundColor={config.colors.primary}
                                    menuWidth={400}
                                    filterOption={createFilter({
                                      stringify: opt => {
                                        const gem = opt.data.gem;
                                        if (gem) {
                                          return _.join([gem.displayName, gem.itemName, gem.itemId], ' ');
                                        }
                                        return _.join([opt.label, opt.value], ' ');
                                      },
                                    })}
                                  />
                                </Box>
                              );
                            }
                            return null;
                          })
                        )}
                      />

                      {/* Enchant */}

                      <If
                        condition={isEnchantable(item.item) && specDetails}
                        then={() => (
                          <Box lg={2}>
                            <NormalSelect
                              label="Enchant"
                              name="itemEnchant"
                              value={item.itemEnchant}
                              options={_.union(
                                [{ children: 'No Enchant', value: '' }],
                                _.map(
                                  getEnhancementsForSlotNameAndSpec(slotMap[item.item.inventoryType], specDetails),
                                  e => ({ children: e.name, value: e.key }),
                                ),
                              )}
                              onChange={e => onChangeEnchant(gearset.id, item.id, e.target.value)}
                              autoComplete="off"
                              color={config.color}
                              backgroundColor={config.colors.primary}
                            />
                          </Box>
                        )}
                      />

                      {/* Embellishment */}

                      <If
                        condition={_.size(embellishments) > 0}
                        then={() => {
                          const embellishmentOptions = _.map(embellishments, embellishment => ({
                            value: embellishment.id,
                            label: (
                              <Flex align="center">
                                <Box mr={1}>
                                  <WowIcon
                                    type="item"
                                    id={embellishment.id}
                                    size={22}
                                    noLink
                                    craftingQuality={embellishment.craftingQuality}
                                    quality={embellishment.quality}
                                  />
                                </Box>
                                <Text size={14} color={config.colors.white}>
                                  {embellishment.name}
                                </Text>
                              </Flex>
                            ),
                            embellishment,
                          }));
                          const value = item.embellishment;
                          return (
                            <Box
                              pr={1}
                              mb={2}
                              ml={1}
                              lg={2}
                              md={2}
                              sm={12}
                              key="embellishment"
                              style={{
                                marginBottom: '20px',
                              }}
                            >
                              <ReactSelect
                                label="Embellishment"
                                name="itemEmbellishment"
                                value={_.find(embellishmentOptions, { value })}
                                options={_.union(
                                  [{ value: '', label: 'None' }],
                                  embellishmentOptions,
                                )}
                                onChange={opt => {
                                  onChangeEmbellishment(gearset.id, item.id, opt.value);
                                }}
                                autoComplete="off"
                                color={config.color}
                                backgroundColor={config.colors.primary}
                                menuWidth={400}
                                filterOption={createFilter({
                                  stringify: opt => {
                                    const { embellishment } = opt.data;
                                    if (embellishment) {
                                      return _.join(
                                        [embellishment.name, embellishment.craftingQuality, embellishment.id],
                                        ' ',
                                      );
                                    }
                                    return _.join([opt.label, opt.value], ' ');
                                  },
                                })}
                              />
                            </Box>
                          );
                        }}
                      />

                    </Flex>
                  );
                })}
                <Button
                  theme="secondary"
                  onClick={() => addItem(gearset.id)}
                  mr={1}
                  p={1}
                >
                  + Add Item
                </Button>
                <Button
                  mr={1}
                  p={1}
                  theme="secondary"
                  onClick={() => duplicateGearset(gearset.id)}
                >
                  Duplicate Gearset
                </Button>
                <SaveGearsetButton
                  onClick={() => saveGearset(gearset)}
                />
                <If condition={gearsets.length > 1}>
                  <Button p={1} theme="warning" onClick={() => removeGearset(gearset.id)}>Remove Gearset</Button>
                </If>
              </Box>
              <Divider />
            </Box>
          );
        })}
        <Flex align="flex-end">
          <Box sm={12} md={2} pr={1}>
            <Button
              theme="secondary"
              onClick={() => addGearset(_.find(savedGearsets, { id: selectedSavedGearset }))}
              mt={1}
            >
              + Add Gearset
            </Button>
          </Box>
          <Box sm={12} md={4} pr={1}>
            <ReactSelect
              options={[
                {
                  label: 'Empty Gearset',
                  value: 'empty',
                  data: {},
                },
                ...savedGearsets.map(savedGearset => ({
                  label: savedGearset.name || savedGearset.defaultName,
                  value: savedGearset.id,
                  data: savedGearset,
                })),
              ]}
              onChange={option => chooseSavedGearset(option ? option.value : null)}
              formatOptionLabel={SavedGearsetRenderer}
              blurInputOnSelect
            />
          </Box>
          {selectedSavedGearset !== 'empty' && (
          <Box style={{ paddingBottom: '4px' }}>
            <Flex>
              <Box align="center">
                <ButtonCircle
                  onClick={() => deleteSavedGearset(selectedSavedGearset)}
                  size={24}
                  color={config.colors.black}
                  backgroundColor={config.colors.warning}
                  style={{
                    minHeight: '24px',
                    lineHeight: '24px',
                  }}
                  mr={1}
                >
                  <Icon name="close" size={16} />
                </ButtonCircle>
              </Box>
              <Box>
                Delete Saved Gearset
              </Box>
            </Flex>
          </Box>
          )}
        </Flex>
      </Box>
    );
  }
}

const mapStateToProps = state => ({
  gearsets: gearsetsSelectors.gearsets(state),
  savedGearsets: savedGearsetsSelectors.savedGearsets(state),
  selectedSavedGearset: selectedSavedGearsetSelectors.selectedSavedGearset(state), // this is ridiculous
  character: simbotSelectors.character(state),
  equippedGear: simbotSelectors.equippedGear(state),
  locale: localeSelectors.locale(state),
  baseActorName: formSelectors.baseActorName(state),
  defaultBaseActorName: formSelectors.defaultBaseActorName(state),
});

const mapDispatchToProps = {
  addGearset: actions.addGearset,
  removeGearset: actions.removeGearset,
  duplicateGearset: actions.duplicateGearset,
  resetGearsets: actions.resetGearsets,
  chooseSavedGearset: actions.chooseSavedGearset,
  saveGearset: actions.saveGearset,
  deleteSavedGearset: actions.deleteSavedGearset,
  addItem: actions.addItem,
  removeItem: actions.removeItem,
  onChangeGearsetName: actions.inputGearsetName,
  onChange: actions.inputItem,
  onChangeSlot: actions.inputSlot,
  onChangeItemLevel: actions.inputItemLevel,
  onChangeGem: actions.inputGem,
  onChangeGemLevel: actions.inputGemLevel,
  onChangeEnchant: actions.inputEnchant,
  onChangeSecondary: actions.inputSecondary,
  onChangeAffix: actions.inputAffix,
  onChangeHand: actions.inputHand,
  onChangeLegendaryPower: actions.inputLegendaryPower,
  onChangeLegendaryStats: actions.inputLegendaryStats,
  onChangeCraftedStats: actions.inputCraftedStats,
  onChangeEmbellishment: actions.inputEmbellishment,
  inputForm: actions.inputForm,
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemChooser);
